import { useCallback } from 'react';

import { randomUUID as uuidv4 } from 'expo-crypto';
import { useRouter } from 'expo-router';
import { differenceBy, intersectionBy } from 'lodash-es';
import { useIntl } from 'react-intl';

import { ICartEntry, ICombo } from '@rbi-ctg/menu';
import { useRedeemReward } from 'components/cart-item/redeem-reward/use-redeem-reward';
import {
  transformUserSelectionsComboToComboSlotSelections,
  transformUserSelectionsModifierToModifierSelection,
} from 'components/product-detail-v2/utils';
import { toaster } from 'components/toaster/toaster';
import { useEnableCartV2Experiment } from 'experiments/use-enable-cart-v2-experiment';
import { useDistance } from 'hooks/geolocation/use-distance';
import { useProductPrice } from 'hooks/menu/use-product-price';
import { useRoute } from 'hooks/navigation/use-route';
import { useCart } from 'hooks/use-cart';
import { useDismissAndReplace } from 'navigation/hooks/use-dismiss-and-navigate';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { IAddToCartSelectionAttributes } from 'state/amplitude/types';
import { mapEntryTypeToOfferType } from 'state/cart/helpers';
import { useFavoritesContext } from 'state/favorites';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { LoyaltyAppliedOffer, isLoyaltyOffer } from 'state/loyalty/types';
import { findRewardInAppliedRewards } from 'state/loyalty/utils';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { useEditingCartEntry } from 'state/product-wizard-v2/hooks/use-editing-cart-entry';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { getCartEntriesBenefit, getCartEntryBenefit } from 'utils/cart/helper';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';
import { routes } from 'utils/routing';
import { IMenuObjectToCartEntry, transformMenuObjectToCartItem } from 'utils/wizard';

export const useProductCart = () => {
  const { formatMessage } = useIntl();
  const { selectFavorite, tempFavorite: currentFavorite } = useFavoritesContext();
  const { isPreConfirmCurbside } = useOrderContext();
  const { setShouldShowPreConfirmCurbsideModal } = useUIContext();
  const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();
  const { isOffer } = useMenuContext();
  const {
    addItemToCart,
    cartEntries,
    removeFromCart,
    repriceCartEntries,
    updateCartEntry,
  } = useCart();
  const {
    selectedProduct,
    productQuantity,
    userSelections,
    manualModifications,
  } = useProductWizardContext();

  const dispatch = useAppDispatch();
  const editingCartEntry = useEditingCartEntry();
  const { totalPrice } = useProductPrice();
  const { incentiveNotInMenu } = useRedeemReward();
  const enableCartV2 = useEnableCartV2Experiment();

  const dismissAndReplace = useDismissAndReplace();
  const { pathname } = useRoute();
  const { rewardBenefitId, isFromCart } = useProductWizardFlow();
  const router = useRouter();

  const { store } = useStoreContext();
  const distanceToStore = useDistance({
    restaurant: store,
    shouldUseUserCoordinatesIfAvailable: true,
  });

  const shouldDisplayMenuV2Toast = !!pathname.startsWith(routes.menu);

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const appliedLoyaltyRewardsArray = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewardsArray
  );
  const selectedOffer = selectedLoyaltyOffer;

  // Adds multiple cart entries to the cart
  const addMultipleToCart = useCallback(
    (
      newCartEntries: ICartEntry[],
      selectionAttributes?: IAddToCartSelectionAttributes | undefined
    ) => {
      const showBulkMessage = newCartEntries.length > 1;

      newCartEntries.forEach(entry => {
        addItemToCart(entry, selectionAttributes, !showBulkMessage);
      });

      if (showBulkMessage) {
        if (shouldDisplayMenuV2Toast) {
          // Menu Screens Toast
          return toaster.addToCartToast(
            formatMessage(
              { id: 'addToCartMultipleSuccess' },
              { itemName: newCartEntries[0].name, quantity: newCartEntries.length - 1 }
            ),
            newCartEntries[0].image
          );
        }

        toaster.positive(
          formatMessage(
            { id: 'addToCartMultipleSuccess' },
            { itemName: newCartEntries[0].name, quantity: newCartEntries.length - 1 }
          )
        );
      }
    },
    [addItemToCart, formatMessage, shouldDisplayMenuV2Toast]
  );

  // Adds or Updates CartEntries
  // Determines which ones are being updated and which ones are new using their cartIds
  const upsertCart = useCallback(
    (
      upsertCartEntries: ICartEntry[],
      selectionAttributes?: IAddToCartSelectionAttributes | undefined
    ) => {
      const newEntries = differenceBy(upsertCartEntries, cartEntries, 'cartId');
      if (newEntries?.length) {
        addMultipleToCart(newEntries, selectionAttributes);
      }

      const existingEntries = intersectionBy(upsertCartEntries, cartEntries, 'cartId');
      // When cart V2 is enabled and the user increase the quantity of an item
      // we are creating a new entries with the same _id.
      // On that case we don't want to show the message when updating an existing same _id entries.
      const differentEntries = differenceBy(existingEntries, newEntries, '_id');
      const skipShowMessage = enableCartV2 && !differentEntries.length;

      existingEntries.forEach((newEntry: ICartEntry) =>
        updateCartEntry(newEntry, newEntry.cartId, skipShowMessage)
      );
    },
    [addMultipleToCart, cartEntries, enableCartV2, updateCartEntry]
  );

  // Cart v2 let user to duplicate an item using the incrementor and apply a reward to the duplicate item. As this could happens in some places in the app not always we have the rewardBenefitId in the params.
  // So we need to check if the rewardBenefitId is available and apply the reward to the cart entry.
  // If cart V2 is disabled we will always use the rewardBenefitId from the params.
  const processRewardRedemption = useCallback(
    (cartEntry: ICartEntry) => {
      const rewardInfo = getAvailableRewardFromCartEntry(cartEntry);

      if (enableCartV2 && !rewardBenefitId && !rewardInfo?.rewardBenefitId) {
        return;
      }

      logRBIEvent({
        name: CustomEventNames.REWARD_ADDED_TO_ORDER,
        type: EventTypes.Other,
        attributes: {
          engineId: rewardInfo?.id ?? '',
          sanityId: rewardInfo?.sanityId ?? '',
          name: rewardInfo?.name ?? '',
          redemptionMode: 'Online',
          distanceToStore,
        },
      });

      const { appliedReward, limitPerOrderMet } = findRewardInAppliedRewards(
        appliedLoyaltyRewardsArray || [],
        rewardInfo?.id || '',
        rewardInfo?.limitPerOrder
      );

      if (appliedReward && limitPerOrderMet) {
        removeFromCart({ cartId: appliedReward.cartId });
      }

      const rewardPayload = {
        rewardBenefitId: enableCartV2
          ? rewardBenefitId || rewardInfo?.rewardBenefitId || ''
          : rewardBenefitId || '',
        cartId: cartEntry.cartId,
        // If a loyalty incentive is not available as a regular menu entry,
        // the quantity must match rewards applied
        ...(incentiveNotInMenu && { quantity: enableCartV2 ? 1 : cartEntry.quantity }),
      };

      dispatch(actions.loyalty.applyReward(rewardPayload));
    },
    [
      getAvailableRewardFromCartEntry,
      enableCartV2,
      rewardBenefitId,
      distanceToStore,
      appliedLoyaltyRewardsArray,
      incentiveNotInMenu,
      dispatch,
      removeFromCart,
    ]
  );

  const handleRewardsRedemption = useCallback(
    (cartEntries: ICartEntry[]) => {
      if (!enableCartV2 && !rewardBenefitId) {
        return;
      }

      const entriesRedemption = getCartEntriesBenefit(
        cartEntries,
        getAvailableRewardFromCartEntry,
        rewardBenefitId
      );

      if (entriesRedemption) {
        entriesRedemption?.forEach(processRewardRedemption);
      }
    },
    [enableCartV2, getAvailableRewardFromCartEntry, processRewardRedemption, rewardBenefitId]
  );

  const handleRewardRedemption = useCallback(
    (cartEntries: ICartEntry[]) => {
      if (rewardBenefitId) {
        const entryRedemption = getCartEntryBenefit(
          cartEntries,
          getAvailableRewardFromCartEntry,
          rewardBenefitId
        );

        if (entryRedemption) {
          processRewardRedemption(entryRedemption);
        }
      }
    },

    [getAvailableRewardFromCartEntry, processRewardRedemption, rewardBenefitId]
  );

  const handleOffers = useCallback(
    ({
      mainEntry,
      upsellEntries,
      modifiersInjected,
      cartEntries,
    }: IMenuObjectToCartEntry & { cartEntries: ICartEntry[] }) => {
      const offerPrice = totalPrice(modifiersInjected);
      if (selectedOffer && isLoyaltyOffer(selectedOffer)) {
        if (!selectedOffer.isStackable) {
          appliedOffers.forEach(offer => {
            if (!offer.isStackable && !!offer.cartId) {
              removeFromCart({ cartId: offer.cartId });
            }
          });
        }
        dispatch(
          actions.loyalty.applyOffer({
            id: selectedOffer.loyaltyEngineId,
            type: (selectedOffer as LoyaltyAppliedOffer).type,
            cartId: mainEntry?.cartId,
            isStackable: selectedOffer.isStackable,
            cmsId: selectedOffer._id,
          })
        );
        dispatch(actions.loyalty.setSelectedOffer(null));
        upsertCart([
          {
            ...mainEntry,
            price: offerPrice,
            // FIXME: VendorConfigs types between LoyaltyOffer and ICartEntry does not match
            // @ts-ignore
            offerVendorConfigs: selectedOffer.vendorConfigs,
            ...(mainEntry && { type: mapEntryTypeToOfferType(mainEntry.type) }),
          },
          ...upsellEntries,
        ]);
      }
    },
    [appliedOffers, dispatch, removeFromCart, selectedOffer, totalPrice, upsertCart]
  );

  const handleTransformMenuObjectToCartItem = useCallback(
    ({ isNewItem, quantity }: { isNewItem: boolean; quantity: number }) => {
      const { mainEntry, upsellEntries, modifiersInjected } = transformMenuObjectToCartItem(
        selectedProduct,
        {
          quantity,
          modifierSelections: transformUserSelectionsModifierToModifierSelection(userSelections),
          pickerSelections: userSelections.pickerAspects,
          comboSlotSelections: transformUserSelectionsComboToComboSlotSelections(
            userSelections.comboSlot,
            selectedProduct as ICombo
          ),
          pathname,
          existingCartId: editingCartEntry?.cartId,
          shouldLogEvents: !!isOffer,
        }
      );

      if (isOffer && selectedOffer) {
        handleOffers({
          mainEntry,
          upsellEntries,
          modifiersInjected,
          cartEntries,
        });
      }

      const cartEntriesWithoutPrice = isOffer
        ? upsellEntries
        : ([
            {
              ...mainEntry,
              cartId: isNewItem ? uuidv4() : editingCartEntry?.cartId || mainEntry?.cartId,
            },
            ...upsellEntries,
          ] as ICartEntry[]);

      return repriceCartEntries(cartEntriesWithoutPrice);
    },
    [
      cartEntries,
      editingCartEntry?.cartId,
      handleOffers,
      isOffer,
      pathname,
      repriceCartEntries,
      selectedOffer,
      selectedProduct,
      userSelections,
    ]
  );

  /**
   * Creates cart entries from the quantity of the product
   * @param quantity - The quantity of new items to create
   * @returns The new cart entries
   */
  const createCartEntriesFromItemQuantity = useCallback(
    (quantity: number) => {
      const cartEntries = [];
      for (let i = 0; i < quantity; i++) {
        const repricedCartEntries = handleTransformMenuObjectToCartItem({
          isNewItem: true,
          quantity: 1,
        });
        cartEntries.push(...repricedCartEntries);
      }
      return cartEntries;
    },
    [handleTransformMenuObjectToCartItem]
  );

  const addToOrder_cartV2 = useCallback(() => {
    let expandedCartEntries: ICartEntry[] = [];

    // If the product quantity is greater than 1, we need to create new cart entries for the same product.
    if (productQuantity > 1) {
      // We don't need to create a new cart entry for the first item because it's already in the cart.
      const quantityToCreate = productQuantity - 1;
      expandedCartEntries = createCartEntriesFromItemQuantity(quantityToCreate);
    }

    const currentCartEntry = handleTransformMenuObjectToCartItem({
      isNewItem: false,
      quantity: 1,
    });

    if (currentFavorite) {
      const cartEntries = handleTransformMenuObjectToCartItem({
        isNewItem: false,
        quantity: productQuantity,
      });

      return selectFavorite({ cartEntries });
    }

    const finalItems = [...currentCartEntry, ...expandedCartEntries];

    if (finalItems.length) {
      const selections: IAddToCartSelectionAttributes = {
        itemModified: manualModifications.modifiers,
        comboSlotSelection: manualModifications.comboSlot,
        pickerAspectSelection: manualModifications.pickerAspects,
      };
      upsertCart(finalItems, selections);
    }

    const isEditingRewardItem = !!rewardBenefitId;
    const currentCartEntryHasRewardApplied =
      currentCartEntry?.[0] &&
      appliedLoyaltyRewardsArray?.some(reward => reward.cartId === currentCartEntry[0].cartId);

    // If current cart entry has a reward applied, apply the rewards to the expanded cart entries too
    if (currentCartEntryHasRewardApplied) {
      handleRewardsRedemption(expandedCartEntries);
    }
    // If we are editing a reward item and none are currently applied, apply the rewards to the final items
    else if (isEditingRewardItem) {
      handleRewardsRedemption(finalItems);
    }

    hapticImpact({ style: HapticsImpactStyle.Heavy });
    return editingCartEntry && isFromCart ? router.dismiss() : dismissAndReplace(routes.menu);
  }, [
    productQuantity,
    handleTransformMenuObjectToCartItem,
    currentFavorite,
    rewardBenefitId,
    appliedLoyaltyRewardsArray,
    editingCartEntry,
    isFromCart,
    router,
    dismissAndReplace,
    createCartEntriesFromItemQuantity,
    selectFavorite,
    manualModifications.modifiers,
    manualModifications.comboSlot,
    manualModifications.pickerAspects,
    upsertCart,
    handleRewardsRedemption,
  ]);

  const addToOrder = useCallback(() => {
    if (isPreConfirmCurbside()) {
      setShouldShowPreConfirmCurbsideModal(true);
      return;
    }

    if (enableCartV2) {
      // Use addToOrder_cartV2 to leverage the new cart functionality introduced in Cart V2,
      // The main difference is that in Cart V2, existing items will not have a quantity greater than 1.
      // Instead, each time a user increments or duplicates an item, it will be added as an individual item.
      // This change aims to improve how users customize items, allowing for more effective customization
      // of individual items.
      // In addition, when a reward is applied to the current entry, it extends to all expanded entries,
      // which is not the case in Cart V1.
      // While we could use the same method,
      // it's safer to split these differences into a new method for now, at least until we decide to move forward with V2.
      return addToOrder_cartV2();
    }

    const cartEntries = handleTransformMenuObjectToCartItem({
      isNewItem: false,
      quantity: productQuantity,
    });

    if (currentFavorite) {
      return selectFavorite({ cartEntries });
    }

    // Only update the cart if we actually have anything to add. (Offers will most likely not have anything here)
    if (cartEntries.length) {
      const selections: IAddToCartSelectionAttributes = {
        itemModified: manualModifications.modifiers,
        comboSlotSelection: manualModifications.comboSlot,
        pickerAspectSelection: manualModifications.pickerAspects,
      };
      upsertCart(cartEntries, selections);
    }

    handleRewardRedemption(cartEntries);

    hapticImpact({ style: HapticsImpactStyle.Heavy });

    // if editing and user coming from the cart
    if (editingCartEntry && isFromCart) {
      return router.dismiss();
    }
    return dismissAndReplace(routes.menu);
  }, [
    isPreConfirmCurbside,
    enableCartV2,
    handleTransformMenuObjectToCartItem,
    productQuantity,
    currentFavorite,
    handleRewardRedemption,
    editingCartEntry,
    isFromCart,
    dismissAndReplace,
    setShouldShowPreConfirmCurbsideModal,
    addToOrder_cartV2,
    selectFavorite,
    manualModifications.modifiers,
    manualModifications.comboSlot,
    manualModifications.pickerAspects,
    upsertCart,
    router,
  ]);

  return {
    addToOrder,
  };
};
